import { useState, useRef, useEffect, useCallback, useMemo } from 'react';

/**
 * Controls playback of an audio file (via object URL) and tracks current time.
 * Highlights the active chord by matching playback time against alignment entries.
 *
 * @param {string|null} audioObjectUrl  - Object URL created from the uploaded file
 * @param {Array}       alignment       - [{time, chord, lineIndex, wordIndex}] — alignment with timing
 * @returns {{ playing, currentTime, duration, activePosition, toggle, seek }}
 */
export function useAudioPlayback(audioObjectUrl, alignment = []) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [activePosition, setActivePosition] = useState(null);
  const rafRef = useRef(null);

  // Sort alignment by time synchronously — always reflects latest prop
  const sortedAlignment = useMemo(
    () => [...alignment].sort((a, b) => (a.time ?? Infinity) - (b.time ?? Infinity)),
    [alignment]
  );
  const alignmentRef = useRef(sortedAlignment);
  alignmentRef.current = sortedAlignment; // sync update on every render

  const findActivePosition = useCallback((t) => {
    const entries = alignmentRef.current;
    // Binary search for the last entry with time <= t
    let lo = 0, hi = entries.length - 1, found = null;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const et = entries[mid].time;
      if (et == null || et > t) {
        hi = mid - 1;
      } else {
        found = { lineIndex: entries[mid].lineIndex, wordIndex: entries[mid].wordIndex, syllableOffset: entries[mid].syllableOffset ?? 0 };
        lo = mid + 1;
      }
    }
    return found;
  }, []);

  // rAF loop: poll audio.currentTime at ~60fps while playing for smooth highlighting
  const startRaf = useCallback(() => {
    const loop = () => {
      const audio = audioRef.current;
      if (!audio) return;
      const t = audio.currentTime;
      setCurrentTime(t);
      setActivePosition(findActivePosition(t));
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
  }, [findActivePosition]);

  const stopRaf = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  // Create / destroy audio element when URL changes
  useEffect(() => {
    if (!audioObjectUrl) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      stopRaf();
      setPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setActivePosition(null);
      return;
    }

    const audio = new Audio();
    audio.preload = 'auto';
    audio.src = audioObjectUrl;
    audioRef.current = audio;

    // durationchange fires repeatedly as browser refines the duration (important for
    // MP4 files where the moov atom may not be at the front of the file).
    const onDurationChange = () => {
      const d = audio.duration;
      if (isFinite(d) && d > 0) setDuration(d);
    };
    const onPlay = () => { setPlaying(true); startRaf(); };
    const onPause = () => { setPlaying(false); stopRaf(); };
    const onEnded = () => {
      setPlaying(false);
      setActivePosition(null);
      stopRaf();
    };

    audio.addEventListener('loadedmetadata', onDurationChange);
    audio.addEventListener('durationchange', onDurationChange);
    audio.addEventListener('canplay', onDurationChange); // final fallback for stubborn codecs
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    if (isFinite(audio.duration) && audio.duration > 0) setDuration(audio.duration);

    return () => {
      audio.pause();
      audio.src = '';    // Clear src so the browser can release the media buffer
      audio.load();      // Required after clearing src to fully abort any pending network load
      audio.removeEventListener('loadedmetadata', onDurationChange);
      audio.removeEventListener('durationchange', onDurationChange);
      audio.removeEventListener('canplay', onDurationChange);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      stopRaf();
      audioRef.current = null;
      setPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setActivePosition(null);
    };
  }, [audioObjectUrl, startRaf, stopRaf]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }
  }, []);

  const seek = useCallback((time) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = time;
    setCurrentTime(time);
    // Update active position immediately on seek
    setActivePosition(findActivePosition(time));
  }, [findActivePosition]);

  return { playing, currentTime, duration, activePosition, toggle, seek, audioRef };
}
